import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, MessageSquare, User, LogOut, ClipboardList, Shield, FolderOpen, DollarSign } from 'lucide-react';
import EmployeeFiles from './EmployeeFiles';
import Communications from '../components/Communications';
import Assignments from '../components/Assignments';
import Approvals from '../components/Approvals';
import MyDocuments from '../components/MyDocuments';
import Payroll from '../components/Payroll';
import './PortalDashboard.css';

const PortalDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('files');

  useEffect(() => {
    // Check if user is logged in
    const sessionData = localStorage.getItem('portal_session');
    if (!sessionData) {
      navigate('/portal/login');
      return;
    }

    const userData = JSON.parse(sessionData);
    setUser(userData);
  }, [navigate]);

  const handleLogout = () => {
    // Clear all portal session data
    localStorage.removeItem('portal_session');
    localStorage.removeItem('portal_user');
    
    // Clear any session cookies
    document.cookie.split(";").forEach((c) => {
      document.cookie = c
        .replace(/^ +/, "")
        .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
    });
    
    // Redirect to login page
    navigate('/portal/login');
  };

  if (!user) {
    return <div>Loading...</div>;
  }

  // Extract name from email
  const getUserName = () => {
    // Special handling for Aaron Gregory (admin)
    if (user.email?.toLowerCase() === 'aaron.gregory@agwesternrail.com') {
      return 'Mr. Gregory';
    }
    
    if (user.name) return user.name;
    const emailPart = user.email.split('@')[0];
    const namePart = emailPart.split('.')[0];
    // Convert "LindsaySchneider" to "Lindsay Schneider"
    return namePart.replace(/([A-Z])/g, ' $1').trim();
  };

  // Check if user is an approver (Madison or Donovan)
  const isApprover = user?.email === 'MadisonBailey.President@agwesternrail.com' ||
                     user?.email === 'DonovanJones.vicepresident@agwesternrail.com';

  return (
    <div className="portal-dashboard">
      {/* Header */}
      <div className="portal-header">
        <div className="portal-header-content">
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            {user.profile_photo && (
              <img 
                src={user.profile_photo} 
                alt={`${getUserName()} profile`}
                style={{
                  width: '50px',
                  height: '50px',
                  borderRadius: '50%',
                  objectFit: 'cover',
                  border: '2px solid #fff',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                }}
              />
            )}
            <h1>Welcome back, {getUserName()}!</h1>
          </div>
          <button className="logout-btn" onClick={handleLogout}>
            <LogOut size={18} /> Logout
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="portal-tabs">
        <button 
          className={`portal-tab ${activeTab === 'files' ? 'active' : ''}`}
          onClick={() => setActiveTab('files')}
        >
          <FileText size={18} /> Files
        </button>
        <button 
          className={`portal-tab ${activeTab === 'communications' ? 'active' : ''}`}
          onClick={() => setActiveTab('communications')}
        >
          <MessageSquare size={18} /> Communications
        </button>
        <button 
          className={`portal-tab ${activeTab === 'assignments' ? 'active' : ''}`}
          onClick={() => setActiveTab('assignments')}
        >
          <ClipboardList size={18} /> Assignments
        </button>
        <button 
          className={`portal-tab ${activeTab === 'documents' ? 'active' : ''}`}
          onClick={() => setActiveTab('documents')}
        >
          <FolderOpen size={18} /> My Documents
        </button>
        <button 
          className={`portal-tab ${activeTab === 'payroll' ? 'active' : ''}`}
          onClick={() => setActiveTab('payroll')}
        >
          <DollarSign size={18} /> Payroll
        </button>
        {isApprover && (
          <button 
            className={`portal-tab ${activeTab === 'approvals' ? 'active' : ''}`}
            onClick={() => setActiveTab('approvals')}
          >
            <Shield size={18} /> Approvals
          </button>
        )}
      </div>

      {/* Content Area */}
      <div className="portal-content">
        {activeTab === 'files' && <EmployeeFiles />}
        {activeTab === 'communications' && <Communications currentUser={user} />}
        {activeTab === 'assignments' && <Assignments currentUser={user} />}
        {activeTab === 'documents' && <MyDocuments currentUser={user} />}
        {activeTab === 'payroll' && <Payroll currentUser={user} />}
        {activeTab === 'approvals' && isApprover && <Approvals currentUser={user} />}
      </div>

      {/* Footer */}
      <div className="portal-footer">
        <img 
          src="https://customer-assets-39nsmqrw.emergentagent.net/job_western-rail-hub/artifacts/3gwy0bes_AGWR_20260514_060630_0000.png"
          alt="AG&W Logo"
          className="footer-logo"
        />
        <span className="footer-divider">|</span>
        <span className="footer-user">{getUserName()}</span>
        <span className="footer-divider">|</span>
        <span className="footer-company">AG&WESTERN Rail Inc.</span>
        <span className="footer-divider">|</span>
        <a href="mailto:support@agwesternrail.com" className="footer-support">
          📧 support@agwesternrail.com
        </a>
      </div>
    </div>
  );
};

export default PortalDashboard;
